import { useTranslation } from '@hbs-us/ui';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { InvoiceListStatus } from '../contractsMirror.js';
import { InvoiceStatusBadge } from './InvoiceStatusBadge.js';

/**
 * The 4-card totals grid on the invoice detail (legacy `InvoiceDetailPage`
 * stat-grid): Invoice Amount · **Paid** (`--chart-3` green) · **Outstanding**
 * (`--destructive` red while anything is owed) · Status (badge). CSS grid + region semantics (NOT a
 * layout table — A11Y-01); numerals in IBM Plex Mono. Every color is a locked
 * token (UI-SPEC Color "Money-emphasis tokens").
 */
export function InvoiceTotalsGrid({
  amount,
  paid,
  outstanding,
  status,
  overdue,
  archived,
  fmt,
}: {
  amount: number;
  paid: number;
  outstanding: number;
  status: InvoiceListStatus;
  overdue?: boolean;
  archived: boolean;
  fmt: MoneyFormatters;
}) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t('invoices:totals.amount')}
      className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('invoices:totals.amount')}
        </div>
        <div className="mt-2 font-mono font-semibold text-foreground text-xl">
          {fmt.formatCurrency(amount)}
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('invoices:totals.paid')}
        </div>
        <div
          className={`mt-2 font-mono font-semibold text-xl ${paid > 0 ? 'text-success' : 'text-foreground'}`}
        >
          {fmt.formatCurrency(paid)}
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('invoices:totals.outstanding')}
        </div>
        <div
          className={`mt-2 font-mono font-semibold text-xl ${outstanding > 0 ? 'text-destructive' : 'text-foreground'}`}
        >
          {fmt.formatCurrency(outstanding)}
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('invoices:totals.status')}
        </div>
        <div className="mt-3">
          <InvoiceStatusBadge status={status} overdue={overdue} archived={archived} />
        </div>
      </div>
    </section>
  );
}
